import type { CompiledStripDeform } from "./types.js";

export interface StripGeometry {
  vertices: Float32Array;
  uvs: Float32Array;
  indices: Uint32Array;
}

function normalized(x: number, y: number): [number, number] {
  const length = Math.hypot(x, y);
  if (length <= 1e-9) throw new Error("Strip deformation requires a non-zero end socket");
  return [x / length, y / length];
}

export function buildStripGeometry(
  deform: CompiledStripDeform,
  endpoint: { x: number; y: number },
  textureSize: { width: number; height: number },
): StripGeometry {
  const sections = deform.sections;
  const [axisX, axisY] = normalized(endpoint.x, endpoint.y);
  const normalX = -axisY;
  const normalY = axisX;
  const targetStartX = -axisX * deform.overlapStart;
  const targetStartY = -axisY * deform.overlapStart;
  const targetEndX = endpoint.x + axisX * deform.overlapEnd;
  const targetEndY = endpoint.y + axisY * deform.overlapEnd;
  const vertices = new Float32Array(sections.length * 6);
  const uvs = new Float32Array(sections.length * 6);

  for (let index = 0; index < sections.length; index += 1) {
    const section = sections[index];
    const previous = sections[Math.max(0, index - 1)].source;
    const next = sections[Math.min(sections.length - 1, index + 1)].source;
    const [sourceTangentX, sourceTangentY] = normalized(next.x - previous.x, next.y - previous.y);
    const sourceNormalX = -sourceTangentY;
    const sourceNormalY = sourceTangentX;
    const targetCenterX = targetStartX + (targetEndX - targetStartX) * section.at;
    const targetCenterY = targetStartY + (targetEndY - targetStartY) * section.at;
    const targetHalfWidth = section.targetWidth / 2;
    const sourceHalfWidth = section.sourceWidth / 2;
    const output = index * 6;

    vertices.set([
      targetCenterX - normalX * targetHalfWidth, targetCenterY - normalY * targetHalfWidth,
      targetCenterX, targetCenterY,
      targetCenterX + normalX * targetHalfWidth, targetCenterY + normalY * targetHalfWidth,
    ], output);
    uvs.set([
      (section.source.x - sourceNormalX * sourceHalfWidth) / textureSize.width,
      (section.source.y - sourceNormalY * sourceHalfWidth) / textureSize.height,
      section.source.x / textureSize.width,
      section.source.y / textureSize.height,
      (section.source.x + sourceNormalX * sourceHalfWidth) / textureSize.width,
      (section.source.y + sourceNormalY * sourceHalfWidth) / textureSize.height,
    ], output);
  }

  const indices = new Uint32Array((sections.length - 1) * 12);
  for (let row = 0; row < sections.length - 1; row += 1) {
    const top = row * 3;
    const bottom = top + 3;
    indices.set([
      top, bottom, bottom + 1,
      top, bottom + 1, top + 1,
      top + 1, bottom + 1, bottom + 2,
      top + 1, bottom + 2, top + 2,
    ], row * 12);
  }
  return { vertices, uvs, indices };
}
